import React, { useState, useEffect, useMemo } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import toast from 'react-hot-toast';
import { useNavigate } from 'react-router-dom';
import { Calendar, ExternalLink } from 'lucide-react';
import { apiGet, apiPut, apiDel } from '../../lib/api.js';
import { useAuth } from '../../store/auth.js';
import {
  DEFAULT_DAYS, DEFAULT_TITLE, DEFAULT_INSTRUCTIONS, DEFAULT_FIELDS,
  DEFAULT_CONFIRMATION_TEXT, DEFAULT_CONFIRMATION_EMAIL, DEFAULT_APPEARANCE, DEFAULT_REMINDER_TEXT,
} from './booking/constants.js';
import { generateTimeSlots, buildDefaultGrid } from './booking/slots.js';
import PageDetailsSection from './booking/PageDetailsSection.jsx';
import BookingPreviewCard, { PREVIEW_QUERY_KEY } from './booking/BookingPreviewCard.jsx';
import AppointmentRulesSection from './booking/AppointmentRulesSection.jsx';
import AdditionalFieldsSection from './booking/AdditionalFieldsSection.jsx';
import AppearanceSection from './booking/AppearanceSection.jsx';
import AvailabilityGridSection from './booking/AvailabilityGridSection.jsx';
import NotificationsSection from './booking/NotificationsSection.jsx';
import CalendarConnectSection from './booking/CalendarConnectSection.jsx';
import DurationChangeModal from './booking/DurationChangeModal.jsx';
import DeleteCalendarModal from './booking/DeleteCalendarModal.jsx';

/** Onboarding step 4 button: moves the agent on to /sync-account. */
function NextStepButton({ onClick, className = '' }) {
  return (
    <button
      type="button"
      data-testid="next-step-sync-account"
      onClick={onClick}
      className={`px-4 py-2 rounded-lg text-xs font-semibold bg-civix-purple hover:bg-civix-purple/90 text-white transition flex items-center gap-1.5 shadow-sm ${className}`}
    >
      Next Step: Sync Account →
    </button>
  );
}

export default function BookingPageTab() {
  const qc = useQueryClient();
  const navigate = useNavigate();
  const { user, agent } = useAuth();

  // ── Form State ──────────────────────────────────────────────────────────
  const [bookingSlug, setBookingSlug] = useState('');
  const [title, setTitle] = useState(DEFAULT_TITLE);
  const [instructions, setInstructions] = useState(DEFAULT_INSTRUCTIONS);
  const [logoUrl, setLogoUrl] = useState('');
  const [redirectUrl, setRedirectUrl] = useState('');
  const [durationMinutes, setDurationMinutes] = useState(30);
  const [bufferMinutes, setBufferMinutes] = useState(0);
  const [startHour, setStartHour] = useState(9);
  const [endHour, setEndHour] = useState(20);
  const [maxBookingDaysOut, setMaxBookingDaysOut] = useState(7);

  // Custom Fields
  const [customFields, setCustomFields] = useState(DEFAULT_FIELDS);

  // Page Appearance
  const [appearance, setAppearance] = useState(DEFAULT_APPEARANCE);

  // Set Availability
  const [availableDays, setAvailableDays] = useState(DEFAULT_DAYS);
  const [availabilityGrid, setAvailabilityGrid] = useState({});

  // General Notifications
  const [notifyViaSms, setNotifyViaSms] = useState(true);
  const [notifyViaEmail, setNotifyViaEmail] = useState(false);
  const [triggerAutomatedAction, setTriggerAutomatedAction] = useState(true);
  const [automatedActionId, setAutomatedActionId] = useState('Scheduled Appointment');
  const [sendReminderText, setSendReminderText] = useState(true);
  const [reminderTexts, setReminderTexts] = useState([DEFAULT_REMINDER_TEXT]);
  const [sendConfirmationText, setSendConfirmationText] = useState(false);
  const [confirmationText, setConfirmationText] = useState(DEFAULT_CONFIRMATION_TEXT);
  const [sendConfirmationEmail, setSendConfirmationEmail] = useState(false);
  const [confirmationEmail, setConfirmationEmail] = useState(DEFAULT_CONFIRMATION_EMAIL);

  // Connect Calendar
  const [saveToCalendarId, setSaveToCalendarId] = useState(agent?.email || 'easton.padden.ffl@gmail.com');
  const [busyDuringCalendars, setBusyDuringCalendars] = useState(agent?.email || 'easton.padden.ffl@gmail.com');
  const [markUnavailableAllDay, setMarkUnavailableAllDay] = useState(true);

  // UI helpers
  const [pendingDuration, setPendingDuration] = useState(null);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [serverSnapshot, setServerSnapshot] = useState(null);

  // ── Fetch agent booking configuration ──────────────────────────────────
  const { data, isLoading } = useQuery({
    queryKey: ['agent-booking-config'],
    queryFn: () => apiGet('/booking/agent-config'),
    staleTime: 30000,
  });

  const defaultEmail = agent?.email || 'easton.padden.ffl@gmail.com';
  // The preview renders what is SAVED (the server's slug), not unsaved edits.
  const savedSlug = data?.bookingSlug || '';

  const applySnapshot = (v) => {
    setBookingSlug(v.bookingSlug);
    setTitle(v.title);
    setInstructions(v.instructions);
    setLogoUrl(v.logoUrl);
    setRedirectUrl(v.redirectUrl);
    setDurationMinutes(v.durationMinutes);
    setBufferMinutes(v.bufferMinutes);
    setStartHour(v.startHour);
    setEndHour(v.endHour);
    setMaxBookingDaysOut(v.maxBookingDaysOut);
    setCustomFields(v.customFields);
    setAppearance(v.appearance);
    setAvailableDays(v.availableDays);
    setAvailabilityGrid(v.availabilityGrid);
    setNotifyViaSms(v.notifyViaSms);
    setNotifyViaEmail(v.notifyViaEmail);
    setTriggerAutomatedAction(v.triggerAutomatedAction);
    setAutomatedActionId(v.automatedActionId);
    setSendReminderText(v.sendReminderText);
    setReminderTexts(v.reminderTexts);
    setSendConfirmationText(v.sendConfirmationText);
    setConfirmationText(v.confirmationText);
    setSendConfirmationEmail(v.sendConfirmationEmail);
    setConfirmationEmail(v.confirmationEmail);
    setSaveToCalendarId(v.saveToCalendarId);
    setBusyDuringCalendars(v.busyDuringCalendars);
    setMarkUnavailableAllDay(v.markUnavailableAllDay);
  };

  // Populate state from server response; the same normalized object is the
  // snapshot that Undo restores and that the dirty check compares against.
  useEffect(() => {
    if (!(data?.ok && data.config)) return;
    const cfg = data.config;
    const notifs = cfg.notifications || {};
    const cal = cfg.calendarConnection || {};

    let grid = cfg.availabilityGrid;
    if (!grid || typeof grid !== 'object') {
      grid = buildDefaultGrid(DEFAULT_DAYS, generateTimeSlots(9, 20, cfg.durationMinutes || 30));
    }

    const snap = {
      bookingSlug: data.bookingSlug || agent?.bookingSlug || 'EastonPadden',
      title: cfg.title || DEFAULT_TITLE,
      instructions: cfg.instructions || DEFAULT_INSTRUCTIONS,
      logoUrl: cfg.logoUrl || '',
      redirectUrl: cfg.redirectUrl || '',
      durationMinutes: cfg.durationMinutes || 30,
      bufferMinutes: cfg.bufferMinutes || 0,
      startHour: cfg.startHour !== undefined ? Number(cfg.startHour) : 9,
      endHour: cfg.endHour !== undefined ? Number(cfg.endHour) : 20,
      maxBookingDaysOut: cfg.maxBookingDaysOut !== undefined ? Number(cfg.maxBookingDaysOut) : 7,
      customFields: Array.isArray(cfg.customFields) && cfg.customFields.length > 0 ? cfg.customFields : DEFAULT_FIELDS,
      appearance: { ...DEFAULT_APPEARANCE, ...(cfg.appearance || {}) },
      availableDays: Array.isArray(cfg.availableDays) && cfg.availableDays.length > 0 ? cfg.availableDays : DEFAULT_DAYS,
      availabilityGrid: grid,
      notifyViaSms: notifs.notifyViaSms !== undefined ? notifs.notifyViaSms : true,
      notifyViaEmail: Boolean(notifs.notifyViaEmail),
      triggerAutomatedAction: notifs.triggerAutomatedAction !== undefined ? notifs.triggerAutomatedAction : true,
      automatedActionId: notifs.automatedActionId || 'Scheduled Appointment',
      sendReminderText: notifs.sendReminderText !== undefined ? notifs.sendReminderText : true,
      reminderTexts: Array.isArray(notifs.reminderTexts) && notifs.reminderTexts.length > 0 ? notifs.reminderTexts : [DEFAULT_REMINDER_TEXT],
      sendConfirmationText: Boolean(notifs.sendConfirmationText),
      confirmationText: notifs.confirmationText || DEFAULT_CONFIRMATION_TEXT,
      sendConfirmationEmail: Boolean(notifs.sendConfirmationEmail),
      confirmationEmail: notifs.confirmationEmail || DEFAULT_CONFIRMATION_EMAIL,
      saveToCalendarId: cal.saveToCalendarId || defaultEmail,
      busyDuringCalendars: cal.busyDuringCalendars || defaultEmail,
      markUnavailableAllDay: cal.markUnavailableAllDay !== undefined ? cal.markUnavailableAllDay : true,
    };
    applySnapshot(snap);
    setServerSnapshot(snap);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [data, agent]);

  const currentState = {
    bookingSlug, title, instructions, logoUrl, redirectUrl, durationMinutes, bufferMinutes,
    startHour, endHour, maxBookingDaysOut, customFields, appearance, availableDays,
    availabilityGrid, notifyViaSms, notifyViaEmail, triggerAutomatedAction, automatedActionId,
    sendReminderText, reminderTexts, sendConfirmationText, confirmationText,
    sendConfirmationEmail, confirmationEmail, saveToCalendarId, busyDuringCalendars,
    markUnavailableAllDay,
  };
  // Grid key order depends on click order, so compare only the sorted checked cells.
  const signature = (v) => JSON.stringify({
    ...v,
    availabilityGrid: Object.keys(v.availabilityGrid || {}).filter((k) => v.availabilityGrid[k]).sort(),
  });
  const isDirty = Boolean(serverSnapshot) && signature(currentState) !== signature(serverSnapshot);

  // Origin for links
  const origin = typeof window !== 'undefined' ? window.location.origin : 'https://app.ringy.com';
  const cleanSlug = bookingSlug
    .trim()
    .replace(/\s+/g, '-')
    .replace(/[^a-zA-Z0-9-]/g, '');
  const publicUrl = cleanSlug ? `${origin}/book/${cleanSlug}` : `${origin}/book/${agent?._id || 'EastonPadden'}`;

  // Restore Default Appearance
  const handleRestoreAppearance = () => {
    setAppearance(DEFAULT_APPEARANCE);
    toast.success('Appearance restored to default');
  };

  // Undo changes back to server snapshot
  const handleUndoChanges = () => {
    if (!serverSnapshot) return;
    applySnapshot(serverSnapshot);
    toast('Changes reverted to last saved state', { icon: '↩️' });
  };

  // Save Mutation
  const saveMutation = useMutation({
    mutationFn: () =>
      apiPut('/booking/agent-config', {
        bookingSlug: cleanSlug,
        title,
        instructions,
        logoUrl,
        redirectUrl,
        durationMinutes: Number(durationMinutes),
        bufferMinutes: Number(bufferMinutes),
        startHour: Number(startHour),
        endHour: Number(endHour),
        maxBookingDaysOut: Number(maxBookingDaysOut),
        customFields,
        availableDays,
        availabilityGrid,
        notifications: {
          notifyViaSms,
          notifyViaEmail,
          triggerAutomatedAction,
          automatedActionId,
          sendReminderText,
          reminderTexts,
          sendConfirmationText,
          confirmationText,
          sendConfirmationEmail,
          confirmationEmail,
        },
        calendarConnection: {
          saveToCalendarId,
          busyDuringCalendars,
          markUnavailableAllDay,
        },
        appearance,
      }),
    onSuccess: () => {
      // Onboarding prompt: offer the next step without forcing a redirect.
      toast.custom((t) => (
        <div className="flex items-center gap-3 px-4 py-3 rounded-lg bg-[#140b22] border border-civix-purple/40 text-white text-sm shadow-xl">
          <span>Booking page saved!</span>
          <button
            type="button"
            onClick={() => { toast.dismiss(t.id); navigate('/sync-account'); }}
            className="px-3 py-1 rounded-md bg-civix-purple hover:bg-civix-purple/90 text-xs font-semibold"
          >
            Continue to Sync Account →
          </button>
        </div>
      ), { duration: 8000 });
      qc.invalidateQueries({ queryKey: ['agent-booking-config'] });
      // The live preview must show the freshly saved hours immediately, not a cached copy.
      qc.invalidateQueries({ queryKey: [PREVIEW_QUERY_KEY] });
    },
    onError: (err) => {
      toast.error(err?.response?.data?.error || err.message || 'Failed to save settings');
    },
  });

  // Delete / Reset Calendar Mutation
  const resetMutation = useMutation({
    mutationFn: () => apiDel('/booking/agent-config'),
    onSuccess: () => {
      toast.success('Booking Calendar settings reset to defaults');
      handleRestoreAppearance();
      setShowDeleteModal(false);
      qc.invalidateQueries({ queryKey: ['agent-booking-config'] });
    },
    onError: (err) => {
      toast.error(err?.message || 'Failed to reset calendar');
      setShowDeleteModal(false);
    },
  });

  // Slots follow the committed duration, so the grid always matches what leads can book.
  const timeSlots = useMemo(
    () => generateTimeSlots(startHour, endHour, durationMinutes),
    [startHour, endHour, durationMinutes],
  );

  const handleDurationRequest = (next) => {
    if (next !== durationMinutes) setPendingDuration(next);
  };

  const confirmDurationChange = () => {
    const next = pendingDuration;
    setDurationMinutes(next);
    setAvailabilityGrid(buildDefaultGrid(availableDays, generateTimeSlots(startHour, endHour, next)));
    setReminderTexts((prev) => prev.map((r) => ({ ...r, num: 15, unit: 'MINUTES' })));
    setPendingDuration(null);
    toast.success(`Availability grid refreshed for ${next}-minute slots. Please review your availability.`);
  };

  const notificationSetters = {
    notifyViaSms: setNotifyViaSms,
    notifyViaEmail: setNotifyViaEmail,
    triggerAutomatedAction: setTriggerAutomatedAction,
    automatedActionId: setAutomatedActionId,
    sendReminderText: setSendReminderText,
    reminderTexts: setReminderTexts,
    sendConfirmationText: setSendConfirmationText,
    confirmationText: setConfirmationText,
    sendConfirmationEmail: setSendConfirmationEmail,
    confirmationEmail: setConfirmationEmail,
  };

  return (
    <div className="space-y-4 max-w-[1440px] mx-auto text-white font-sans pb-16 [color-scheme:dark]">
      <div className="bg-[#0e0817] rounded-xl shadow-lg border border-white/10 overflow-hidden">
        {/* Card header */}
        <div className="px-6 py-4 flex items-center justify-between border-b border-white/10 bg-gradient-to-r from-civix-purple/10 to-transparent">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 rounded-md bg-civix-purple/15 flex items-center justify-center text-civix-purpleSoft border border-civix-purple/30">
              <Calendar className="w-5 h-5" />
            </div>
            <h2 className="text-xl font-bold text-white tracking-tight font-display">Appointment Booking Page</h2>
          </div>
          <div className="flex items-center gap-3">
            <NextStepButton onClick={() => navigate('/sync-account')} />
            <a
              href={publicUrl}
              target="_blank"
              rel="noreferrer"
              className="text-xs font-semibold text-civix-cyan hover:text-white flex items-center gap-1 transition"
            >
              <ExternalLink className="w-3.5 h-3.5" />
              Preview Live
            </a>
            <button
              type="button"
              data-testid="delete-appointment-calendar"
              onClick={() => setShowDeleteModal(true)}
              className="text-xs font-bold text-rose-400 hover:text-rose-300 uppercase tracking-wider transition cursor-pointer"
            >
              DELETE CALENDAR
            </button>
          </div>
        </div>

        <div className="p-6 grid grid-cols-1 lg:grid-cols-12 gap-8 items-start border-b border-white/10">
          <PageDetailsSection
            origin={origin}
            publicUrl={publicUrl}
            bookingSlug={bookingSlug}
            setBookingSlug={setBookingSlug}
            logoUrl={logoUrl}
            setLogoUrl={setLogoUrl}
            title={title}
            setTitle={setTitle}
            instructions={instructions}
            setInstructions={setInstructions}
            redirectUrl={redirectUrl}
            setRedirectUrl={setRedirectUrl}
          />
          <BookingPreviewCard slug={savedSlug} appearance={appearance} logoUrl={logoUrl} title={title} instructions={instructions} />
        </div>

        <div className="p-6 grid grid-cols-1 md:grid-cols-3 gap-8 items-start border-b border-white/10">
          <AppointmentRulesSection
            durationMinutes={durationMinutes}
            onDurationRequest={handleDurationRequest}
            startHour={startHour}
            setStartHour={setStartHour}
            endHour={endHour}
            setEndHour={setEndHour}
            maxBookingDaysOut={maxBookingDaysOut}
            setMaxBookingDaysOut={setMaxBookingDaysOut}
            timezone={agent?.timezone}
          />
          <AdditionalFieldsSection customFields={customFields} setCustomFields={setCustomFields} />
          <AppearanceSection appearance={appearance} setAppearance={setAppearance} onRestore={handleRestoreAppearance} />
        </div>

        <div className="p-6 grid grid-cols-1 lg:grid-cols-12 gap-8 items-start border-b border-white/10">
          <AvailabilityGridSection
            availableDays={availableDays}
            setAvailableDays={setAvailableDays}
            availabilityGrid={availabilityGrid}
            setAvailabilityGrid={setAvailabilityGrid}
            timeSlots={timeSlots}
          />
          <NotificationsSection
            values={{
              notifyViaSms, notifyViaEmail, triggerAutomatedAction, automatedActionId,
              sendReminderText, reminderTexts, sendConfirmationText, confirmationText,
              sendConfirmationEmail, confirmationEmail,
            }}
            onChange={(key, value) => notificationSetters[key](value)}
          />
        </div>

        <CalendarConnectSection
          email={defaultEmail}
          saveToCalendarId={saveToCalendarId}
          setSaveToCalendarId={setSaveToCalendarId}
          busyDuringCalendars={busyDuringCalendars}
          setBusyDuringCalendars={setBusyDuringCalendars}
          markUnavailableAllDay={markUnavailableAllDay}
          setMarkUnavailableAllDay={setMarkUnavailableAllDay}
        />

        <div className="p-6 bg-white/[0.03] flex items-center justify-end gap-3">
          <NextStepButton onClick={() => navigate('/sync-account')} className="mr-auto" />
          <a
            href={publicUrl}
            target="_blank"
            rel="noreferrer"
            data-testid="preview-booking-page-bottom"
            className="px-5 py-2 rounded text-xs font-bold uppercase tracking-wider text-civix-purpleSoft border border-civix-purple/50 hover:bg-civix-purple/10 transition inline-flex items-center gap-1.5"
          >
            <ExternalLink className="w-3.5 h-3.5" />
            Preview Your Booking Page
          </a>
          <button
            type="button"
            data-testid="undo-changes-button"
            onClick={handleUndoChanges}
            disabled={!isDirty}
            className="px-5 py-2 rounded text-xs font-bold uppercase tracking-wider text-white/80 bg-white/10 hover:bg-white/15 transition cursor-pointer disabled:opacity-40 disabled:cursor-not-allowed"
          >
            UNDO CHANGES
          </button>
          <button
            type="button"
            data-testid="save-appointment-calendar"
            onClick={() => saveMutation.mutate()}
            disabled={saveMutation.isPending || !isDirty}
            className="px-6 py-2 rounded text-xs font-bold uppercase tracking-wider text-white bg-civix-purple hover:bg-civix-purple/80 transition cursor-pointer disabled:opacity-40 disabled:cursor-not-allowed"
          >
            {saveMutation.isPending ? 'SAVING…' : 'SAVE CALENDAR'}
          </button>
        </div>
      </div>

      <DurationChangeModal
        pendingDuration={pendingDuration}
        onCancel={() => setPendingDuration(null)}
        onConfirm={confirmDurationChange}
      />
      <DeleteCalendarModal
        open={showDeleteModal}
        busy={resetMutation.isPending}
        onClose={() => setShowDeleteModal(false)}
        onConfirm={() => resetMutation.mutate()}
      />
    </div>
  );
}
